import { type Ref, computed, unref, useStore } from "@nuxtjs/composition-api";

import { useAmplitude, useFeatureDetection, useI18n, useLinkService, useTranslate, useVuetify } from "@/composables";
import { getLtksStore } from "@/store/ltks";
import { getRetailerProductsStore } from "@/store/retailer-products";
import { sortByNumberAscending } from "@/utils/transform/array";
import { toCurrency } from "@/utils/transform/text";

const filterDefaultOption = (option: RetailerProductOptions): boolean => {
  return !(option.values.length === 1 && option.name === "Title" && option.values[0] === "Default Title");
};

const smallImageSize = "88";
const heroImageSize = "450";
const fullImageHeight = "736";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useRetailerProduct
 * @param    {Ref<string> | string} retailerProductId
 * @desc     A composable that returns a retailer product from the retailer products store
 *
 * @summary  Composable used to retrieve a retailer product.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useRetailerProduct = (retailerProductId: Ref<string> | string) => {
  const store = useStore();

  const retailerProduct = computed(() => getRetailerProductsStore(store).retailerProducts[unref(retailerProductId)]);

  return { retailerProduct };
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useRetailerProductPrices
 * @param    {Ref<string> | string} retailerProductId
 * @param    {Ref<string> | string} retailerProductVariantId
 * @desc     A composable that calculates the prices for a retailer product
 *           given a retailer product ID and retailer product variant ID.
 *
 * @summary  Composable used to calculate pricing for a retailer product.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useRetailerProductPrices = (
  retailerProductId: Ref<string> | string,
  retailerProductVariantId?: Ref<string> | string,
) => {
  const i18n = useI18n();
  const { retailerProduct } = useRetailerProduct(retailerProductId);
  const retailerProductVariant = computed(() => {
    return retailerProduct?.value?.variants?.find(({ id }) => id === unref(retailerProductVariantId));
  });

  const isPriceRange = computed(() => {
    if (!retailerProduct.value) {
      return false;
    }
    const { priceMin, priceMax } = retailerProduct.value;
    return priceMin !== priceMax;
  });

  const price = computed(() => {
    if (!retailerProduct.value) {
      return "";
    }
    const { priceMin, priceMax, currency } = retailerProduct.value;
    // If the min and max price are not the same, use a price range
    if (isPriceRange.value) {
      const formattedPriceMin = toCurrency(priceMin, { locales: i18n.locale, currency });
      const formattedPriceMax = toCurrency(priceMax, { locales: i18n.locale, currency });
      return `${formattedPriceMin} - ${formattedPriceMax}`;
    } else {
      return toCurrency(priceMin, { locales: i18n.locale, currency });
    }
  });

  const salePrice = computed(() => {
    if (!retailerProduct.value) {
      return "";
    }
    const { priceMin, priceMax, salePriceMin, salePriceMax, currency } = retailerProduct.value;

    const parsedPriceMin = Number(priceMin);
    const parsedPriceMax = Number(priceMax);
    // Ensure that the sale price is no greater than the original price
    const parsedSalePriceMin = Math.min(parsedPriceMin, Number(salePriceMin));
    const parsedSalePriceMax = Math.min(parsedPriceMax, Number(salePriceMax));

    const formattedSalePriceMin = toCurrency(parsedSalePriceMin, {
      locales: i18n.locale,
      currency,
    });
    const formattedSalePriceMax = toCurrency(parsedSalePriceMax, {
      locales: i18n.locale,
      currency,
    });
    // If the sale price min/max is less than the original price min/max and is a sale range, show the sale price range
    if (
      parsedSalePriceMin < parsedPriceMin &&
      parsedSalePriceMax < parsedPriceMax &&
      parsedSalePriceMin !== parsedSalePriceMax
    ) {
      return `${formattedSalePriceMin} - ${formattedSalePriceMax}`;
    }
    // If the sale price min is less than the original price min, show the sale price min
    if (parsedSalePriceMin < parsedPriceMin) {
      return formattedSalePriceMin;
    }
    // Else, don't show the sale price
    return "";
  });

  const savePercentage = computed(() => {
    if (!retailerProduct.value) {
      return "";
    }
    const { priceMin, salePriceMin } = retailerProduct.value;
    if (!salePrice.value) {
      return "";
    }
    return Intl.NumberFormat(i18n.locale, {
      style: "percent",
    }).format((Number(priceMin) - Number(salePriceMin)) / Number(priceMin));
  });

  const variantPrice = computed(() => {
    if (!retailerProduct.value || !retailerProductVariant.value) {
      return "";
    }
    const { currency } = retailerProduct.value;
    const { price } = retailerProductVariant.value;
    return toCurrency(price, { locales: i18n.locale, currency });
  });

  const variantSalePrice = computed(() => {
    if (!retailerProduct.value || !retailerProductVariant.value) {
      return "";
    }
    const { currency } = retailerProduct.value;
    const { price, salePrice } = retailerProductVariant.value;

    const parsedPrice = Number(price);
    const parsedSalePrice = Number(salePrice);

    if (parsedSalePrice >= parsedPrice) {
      return "";
    }
    return toCurrency(parsedSalePrice, { locales: i18n.locale, currency });
  });

  const variantSavePercentage = computed(() => {
    if (!retailerProductVariant.value) {
      return "";
    }
    const { price, salePrice } = retailerProductVariant.value;
    if (!variantSalePrice.value) {
      return "";
    }
    return Intl.NumberFormat(i18n.locale, {
      style: "percent",
    }).format((Number(price) - Number(salePrice)) / Number(price));
  });

  return { isPriceRange, price, salePrice, savePercentage, variantPrice, variantSalePrice, variantSavePercentage };
};

export const useRetailerProductImages = (
  catalogRetailerProductId: Ref<string> | string,
  ltkId?: Ref<string> | string,
) => {
  const store = useStore();
  const linkService = useLinkService();
  const featureDetection = useFeatureDetection();
  const vuetify = useVuetify();

  const catalogRetailerProduct = computed(
    () => getRetailerProductsStore(store).retailerProducts[unref(catalogRetailerProductId)],
  );
  const ltk = computed(() => getLtksStore(store).ltks[unref(ltkId ?? "")]);

  const sizeChartImageUrl = computed(() => catalogRetailerProduct.value?.sizeChart?.imageUrl);

  const imageUrls = computed(() => {
    if (!catalogRetailerProduct.value) {
      return [];
    }
    const images = [
      catalogRetailerProduct.value.imageUrl,
      ...(catalogRetailerProduct.value.additionalImageUrls ?? []),
      ...(catalogRetailerProduct.value.variants ?? []).map(({ imageUrl }) => imageUrl),
      ltk.value?.heroImage ?? "",
    ].filter((imageUrl): imageUrl is string => Boolean(imageUrl));
    return [...new Set(images)];
  });

  const smallImagesProps = computed(() => {
    return imageUrls.value.map((image) => {
      const imageUrl = linkService.getImageLink({
        url: image,
        width: smallImageSize,
        height: smallImageSize,
        useDefaultDpr: false,
      });
      return {
        src: imageUrl + `&dpr=${featureDetection.dpr}`,
        srcSet: `${imageUrl}&dpr=1 1x, ${imageUrl}&dpr=2 2x, ${imageUrl}&dpr=3 3x`,
        width: 88,
        height: 88,
        crossorigin: false,
      };
    });
  });

  const heroImagesProps = computed(() => {
    return imageUrls.value.map((image) => {
      const imageUrl = linkService.getImageLink({
        url: image,
        width: heroImageSize,
        height: heroImageSize,
        useDefaultDpr: false,
      });
      return {
        src: imageUrl + `&dpr=${featureDetection.dpr}`,
        srcSet: `${imageUrl}&dpr=1 1x, ${imageUrl}&dpr=2 2x, ${imageUrl}&dpr=3 3x`,
        aspect: 1,
        crossorigin: false,
        rounded: vuetify.breakpoint.smAndUp,
      };
    });
  });

  const fullImagesProps = computed(() => {
    return imageUrls.value.map((image) => {
      const imageUrl = linkService.getImageLink({
        url: image,
        height: fullImageHeight.toString(),
        crop: false,
        useDefaultDpr: false,
      });
      return {
        src: imageUrl + `&dpr=${featureDetection.dpr}`,
        srcSet: `${imageUrl}&dpr=1 1x, ${imageUrl}&dpr=2 2x, ${imageUrl}&dpr=3 3x`,
        contain: true,
      };
    });
  });

  return { imageUrls, smallImagesProps, heroImagesProps, fullImagesProps, sizeChartImageUrl };
};

interface PolicyProps {
  handle: string;
  on: {
    "open-modal-click": () => void;
  };
  props: {
    title: string;
    body: string;
    cta: string;
  };
}

export const useRetailerProductPolicies = (catalogRetailerProductId: Ref<string> | string) => {
  const amplitude = useAmplitude();
  const store = useStore();
  const { t } = useTranslate();

  const catalogRetailerProduct = computed(
    () => getRetailerProductsStore(store).retailerProducts[unref(catalogRetailerProductId)],
  );

  const policies = computed(() => {
    if (!catalogRetailerProduct.value) {
      return [];
    }

    return catalogRetailerProduct.value.policies
      .filter(({ handle }) => handle === "shipping-policy" || handle === "refund-policy")
      .map<PolicyProps>(({ handle, body }) => {
        if (handle === "shipping-policy") {
          return {
            handle,
            props: {
              title: t("Shipping Information").value,
              body,
              cta: t("View shipping policy").value,
            },
            on: {
              "open-modal-click": () => amplitude?.sendEvent("/page/product-details/view-shipping-policy"),
            },
          };
        }
        return {
          handle,
          props: {
            title: t("Return & Exchanges").value,
            body,
            cta: t("View returns policy").value,
          },
          on: {
            "open-modal-click": () => amplitude?.sendEvent("/page/product-details/view-returns-policy"),
          },
        };
      });
  });

  return { policies };
};

export const useRetailerProductOptions = (catalogRetailerProductId: Ref<string> | string) => {
  const store = useStore();

  const catalogRetailerProduct = computed(
    () => getRetailerProductsStore(store).retailerProducts[unref(catalogRetailerProductId)],
  );

  const options = computed(() => {
    if (!catalogRetailerProduct.value) {
      return [];
    }
    return (
      [...catalogRetailerProduct.value.options]
        .sort((a, b) => sortByNumberAscending(a.position, b.position))
        // By default the Shopify API may insert a default option. This option needs to be filtered since we don't want to show it to the user.
        .filter(filterDefaultOption)
    );
  });

  return {
    options,
  };
};
